import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle2, XCircle, Loader2, ShieldCheck, Clock, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HealthCheckTab = ({ language = 'es' }) => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const tx = (es, en) => language === 'es' ? es : en;

  const runCheck = async () => {
    setRunning(true);
    setReport(null);
    try {
      const response = await axios.post(`${API}/admin/health-check`, {}, { timeout: 130000 });
      setReport(response.data.report);
      if (response.data.report?.failed === 0) {
        toast.success(tx('Todos los tests pasados', 'All tests passed'));
      } else {
        toast.error(tx(`${response.data.report.failed} tests fallidos`, `${response.data.report.failed} tests failed`));
      }
    } catch (error) {
      toast.error(tx('Error ejecutando health check', 'Error running health check'));
    } finally {
      setRunning(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="health-check-tab">
      <Card className="border-l-4 border-l-emerald-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            {tx('Health Check - Test Automático', 'Health Check - Automatic Test')}
          </CardTitle>
          <CardDescription>
            {tx(
              'Ejecuta tests automáticos en todas las funcionalidades críticas de la app. Úsalo después de cada deploy.',
              'Run automatic tests on all critical app functionalities. Use after each deploy.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={runCheck}
            disabled={running}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="run-health-check-btn"
          >
            {running ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{tx('Ejecutando tests...', 'Running tests...')}</>
            ) : (
              <><ShieldCheck className="h-4 w-4 mr-2" />{tx('Ejecutar Health Check', 'Run Health Check')}</>
            )}
          </Button>

          {running && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
              {tx('Testeando login, productos, SEO Prospector, email, Ahrefs, API Sync, pedidos, facturas, usuarios, CRM, wallet, password reset... (~25s)',
                  'Testing login, products, SEO Prospector, email, Ahrefs, API Sync, orders, invoices, users, CRM, wallet, password reset... (~25s)')}
            </div>
          )}
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Summary */}
          <Card className={`border-l-4 ${report.failed === 0 ? 'border-l-green-500' : 'border-l-red-500'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {report.failed === 0 ? (
                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-7 w-7 text-green-600" />
                    </div>
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-7 w-7 text-red-600" />
                    </div>
                  )}
                  <div>
                    <p className="text-xl font-bold">
                      {report.passed}/{report.total} {tx('tests pasados', 'tests passed')}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {report.elapsed_seconds}s - {formatDate(report.timestamp)}
                    </p>
                  </div>
                </div>
                <Badge variant={report.failed === 0 ? 'default' : 'destructive'} className="text-base px-4 py-1">
                  {report.failed === 0 ? tx('TODO OK', 'ALL OK') : `${report.failed} ${tx('FALLOS', 'FAILURES')}`}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{tx('Detalle de tests', 'Test details')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {(report.results || []).map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-2 rounded text-sm ${
                      r.status ? 'bg-green-50' : 'bg-red-50 border border-red-200'
                    }`}
                    data-testid={`health-test-${i}`}
                  >
                    <div className="flex items-center gap-2">
                      {r.status ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600 shrink-0" />
                      )}
                      <span className={r.status ? 'text-green-800' : 'text-red-800 font-medium'}>
                        {r.test}
                      </span>
                    </div>
                    {r.detail && (
                      <span className="text-xs text-muted-foreground ml-2 truncate max-w-[300px]">
                        {r.detail}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default HealthCheckTab;
